
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../../../components/navbar";
import Footer from "../../../../../../../components/footer";
import Button from "../../../../../../../components/Button";
import FormInput from "../../../../../../../components/FormInput";
import Pagination from "../../../../../../../components/Pagination";
import styles from "../../../../css/Transactions/TransactionOptions/AccountSummary/DebtorsAccountSummary.module.css";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getPatientCreditTransactions } from "../../../../../../../api/frontShop/DebtorsAPI";

function formatCurrency(amount) {
  const n = Number(amount || 0);
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function titleCase(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateLabel(month) {
  try {
    const d = month ? new Date(`${month}-01T00:00:00`) : new Date();
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return "";
  }
}

export default function DebtorsAccountSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const transaction = (() => {
    const fromState = location.state?.transaction || null;
    if (fromState) {
      try {
        sessionStorage.setItem("debtors_selected_transaction", JSON.stringify(fromState));
      } catch {}
      return fromState;
    }
    try {
      const raw = sessionStorage.getItem("debtors_selected_transaction");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const patientId = transaction?.patientid != null ? Number(transaction.patientid) : null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [transactions, setTransactions] = useState([]);

  const [month, setMonth] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    setCurrentPage(1);
  }, [month]);

  const summaryModel = useMemo(() => {
    const filtered = month
      ? transactions.filter((t) => {
          const d = String(t?.transactiondate || "");
          return d.startsWith(`${month}-`);
        })
      : transactions;

    const ordered = [...filtered].sort((a, b) => {
      const da = new Date(a?.transactiondate || 0).getTime();
      const db = new Date(b?.transactiondate || 0).getTime();
      if (da !== db) return da - db;
      const aId = Number(a?.transactionid ?? a?.transactionId ?? a?.id ?? 0);
      const bId = Number(b?.transactionid ?? b?.transactionId ?? b?.id ?? 0);
      return aId - bId;
    });

    const rows = ordered.map((t) => {
      const txType = String(t?.type || "").toLowerCase();
      const amount = Number(t?.transactionamount || 0);
      const isInvoice = txType === "debit";
      const txId = Number(t?.transactionid ?? t?.transactionId ?? t?.id ?? 0);
      return {
        txId,
        date: t?.transactiondate ? String(t.transactiondate).slice(0, 10) : "",
        type: isInvoice ? "Invoice" : "Payment",
        details: t?.details || "",
        debit: isInvoice ? amount : null,
        credit: isInvoice ? null : amount,
        balance: Number(t?.balance || 0),
      };
    });

    const openingBalance = rows.length ? Number(rows[0]?.balance || 0) : 0;
    const totalPurchases = rows.reduce((sum, r) => sum + (r.debit == null ? 0 : Number(r.debit) || 0), 0);
    const totalPaid = rows.reduce((sum, r) => sum + (r.credit == null ? 0 : Number(r.credit) || 0), 0);
    const outstanding = rows.length ? Number(rows[rows.length - 1]?.balance || 0) : 0;

    return {
      customerName: transaction?.name || "Carlos Gonzalez",
      accountType: titleCase(transaction?.method || "Credit") || "Credit",
      dateLabel: formatDateLabel(month),
      rows,
      openingBalance,
      totalPurchases,
      totalPaid,
      outstanding,
    };
  }, [month, transaction, transactions]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!patientId || !Number.isFinite(patientId)) {
        setTransactions([]);
        setError("Missing patient");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await getPatientCreditTransactions(patientId);
        const json = res?.data;
        if (cancelled) return;
        setTransactions(Array.isArray(json?.data) ? json.data : []);
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [patientId, location.state?.refreshToken]);

  const totalPages = Math.ceil(summaryModel.rows.length / itemsPerPage) || 1;
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return summaryModel.rows.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, itemsPerPage, summaryModel.rows]);

  function downloadPdf() {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(14);
    doc.text("MediIntel", pageWidth / 2, 48, { align: "center" });

    doc.setFontSize(12);
    doc.text("Account Summary", 40, 86);
    doc.setFontSize(10);
    doc.text(`Date: ${summaryModel.dateLabel}`, pageWidth - 40, 86, { align: "right" });

    doc.setFontSize(10);
    doc.text(summaryModel.customerName || "-", 40, 116);
    doc.text(`Account Type: ${summaryModel.accountType}`, 40, 132);

    autoTable(doc, {
      startY: 160,
      head: [["Date", "Type", "Details", "Debit", "Credit", "Balance"]],
      body: summaryModel.rows.map((r) => [
        r.date,
        r.type,
        r.details,
        r.debit == null ? "-" : formatCurrency(r.debit),
        r.credit == null ? "-" : formatCurrency(r.credit),
        formatCurrency(r.balance),
      ]),
      styles: { fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: [228, 214, 246], textColor: [17, 24, 39], fontStyle: "bold" },
      theme: "grid",
    });

    const endY = doc.lastAutoTable?.finalY || 160;
    const boxX = pageWidth - 220;
    const boxY = endY + 55;
    doc.setDrawColor(228, 214, 246);
    doc.roundedRect(boxX, boxY, 180, 96, 8, 8);

    doc.setFontSize(10);
    doc.text("SUMMARY", boxX + 12, boxY + 18);
    doc.setFontSize(9);
    doc.text("Opening Balance", boxX + 12, boxY + 40);
    doc.text(formatCurrency(summaryModel.openingBalance), boxX + 168, boxY + 40, { align: "right" });
    doc.text("Total Purchases", boxX + 12, boxY + 56);
    doc.text(formatCurrency(summaryModel.totalPurchases), boxX + 168, boxY + 56, { align: "right" });
    doc.text("Total Paid", boxX + 12, boxY + 72);
    doc.text(formatCurrency(summaryModel.totalPaid), boxX + 168, boxY + 72, { align: "right" });

    doc.setFontSize(10);
    doc.text("Outstanding", boxX + 12, boxY + 92);
    doc.text(formatCurrency(summaryModel.outstanding), boxX + 168, boxY + 92, { align: "right" });

    doc.save(`account-summary-${transaction?.id || "debtors"}.pdf`);
  }

  function proceedToPayment() {
    try {
      sessionStorage.setItem("debtors_total", String(summaryModel.outstanding || 0));
      if (patientId) sessionStorage.setItem("debtors_patientid", String(patientId));
    } catch {}
    navigate("/frontshop/debtors/payment-types", {
      state: {
        total: summaryModel.outstanding,
        fromDebtors: true,
        transaction,
        month,
        returnTo: location.pathname,
      },
    });
  }

  return (
    <div className={styles.wrapper}>
      <Navbar backTo="/frontshop/debtors/transactions/options" />

      <main className={styles.main}>
        <div className={styles.topRow}>
          <div className={styles.monthSelect} aria-label="Select month">
            <span style={{ marginRight: 16, fontWeight: 600, color: "#6b7280", fontSize: 14 }}></span>
            <div className={styles.monthInputWrap}>
              <FormInput
                label=""
                type="month"
                name="accountSummaryMonth"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className={`${styles.monthInput} ${!month ? styles.monthInputEmpty : ""}`}
              />
              {!month ? <span className={styles.allTimeOverlay}>All time</span> : null}
            </div>
          </div>
        </div>

        <section className={styles.card}>
          <div className={styles.band}>MediIntel</div>

          <div className={styles.headerRow}>
            <div className={styles.title}>Account Summary</div>
            <div className={styles.headerRight}>Date: {summaryModel.dateLabel}</div>
          </div>

          <div className={styles.accountBox}>
            <div className={styles.customerName}> {summaryModel.customerName}</div>
            <div className={styles.accountType}>Account Type: {summaryModel.accountType}</div>
          </div>

          <div className={styles.contentGrid}>
            <div className={styles.tableSection}>
              <div className={styles.tableWrap}>
                <div className={styles.tableHeader}>
                  <div>Date</div>
                  <div>Type</div>
                  <div>Details</div>
                  <div className={styles.cellRight}>Debit</div>
                  <div className={styles.cellRight}>Credit</div>
                  <div className={styles.cellRight}>Balance</div>
                </div>
                <div className={styles.tableBody}>
                  {error ? <div className={styles.error}>{error}</div> : null}
                  {loading ? <div className={styles.loading}>Loading...</div> : null}
                  {paginatedRows.map((r, idx) => (
                    <div key={`${r.date}-${r.txId || 0}`} className={styles.tableRow}>
                      <div>{r.date}</div>
                      <div>
                        <span className={`${styles.pill} ${r.type === "Payment" ? styles.pillPayment : styles.pillInvoice}`}>{r.type}</span>
                      </div>
                      <div>{r.details}</div>
                      <div className={styles.cellRight}>{r.debit == null ? "-" : formatCurrency(r.debit)}</div>
                      <div className={styles.cellRight}>{r.credit == null ? "-" : formatCurrency(r.credit)}</div>
                      <div className={styles.cellRight}>{formatCurrency(r.balance)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.paginationWrap}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            </div>

            <aside className={styles.summaryCard}>
              <div className={styles.summaryTitle}>SUMMARY</div>
              <div className={styles.summaryRow}>
                <div className={styles.summaryLabel}>Opening Balance</div>
                <div className={styles.summaryValue}>{formatCurrency(summaryModel.openingBalance)}</div>
              </div>
              <div className={styles.summaryRow}>
                <div className={styles.summaryLabel}>Total Purchases</div>
                <div className={styles.summaryValue}>{formatCurrency(summaryModel.totalPurchases)}</div>
              </div>
              <div className={styles.summaryRow}>
                <div className={styles.summaryLabel}>Total Paid</div>
                <div className={styles.summaryValue}>{formatCurrency(summaryModel.totalPaid)}</div>
              </div>
              <div className={styles.summaryDivider} />
              <div className={styles.summaryRowTotal}>
                <div className={styles.summaryLabelTotal}>Outstanding</div>
                <div className={styles.summaryValueTotal}>{formatCurrency(summaryModel.outstanding)}</div>
              </div>
            </aside>
          </div>

          <div className={styles.bottomRow}>
            <div className={styles.totalDue}>
              <div className={styles.totalDueLabel}>TOTAL DUE</div>
              <div className={styles.totalDueValue}>{formatCurrency(summaryModel.outstanding)}</div>
            </div>

            <div className={styles.actions}>
              <Button variant="primary" className={styles.downloadBtn} onClick={downloadPdf}>
                Download
              </Button>
              <Button variant="primary" className={styles.proceedBtn} onClick={proceedToPayment}>
                Proceed to payment
              </Button>
            </div>
          </div>
        </section>
      </main>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
